<script setup lang="ts">
import type {
  GradeList,
  GradeSpecification,
  PastGradeListDetail,
  GradeListCategory,
} from '#shared/interfaces/gradeList'
import { TITLE_PAGE, TRANSITION_SOURCE } from '#shared/constant/constant'
import type {
  PastModelInfo,
  PastModels,
} from '#shared/interfaces/model-top/pastModels'
import type {
  Ratings,
  RatingsSearch,
} from '#shared/interfaces/model-grade/ratings'
import type {
  ReviewsSearch,
  Reviews,
} from '#shared/interfaces/model-grade/reviews'
import type { Model, Models } from '#shared/interfaces/model-top/models'
import type {
  CheckUsedCars,
  UsedCarContents,
} from '#shared/interfaces/model-top/checkUsedCars'
import type { CarInfo } from '@/interfaces/car-info-cassette/carInfo'
import type {
  SendBrandDetail,
  SendModelGradesDetail,
  SendCondition,
  SendCar,
} from '@/interfaces/searchUsedCar'
import type {
  UrlParametersItem,
  UrlParametersResponse,
} from '#shared/interfaces/top/urlParameters'
import { useShareContents } from '@/composables/useShareContents'
import { SCHEMA_URL } from '#shared/constant/constant'
import { postWindowBridge } from '@/utils/windowBridge'
import { buildAsyncDataKey } from '@/utils/asyncDataKey'

const isLoading = ref(true)
const ratingsItem = ref<Ratings>()
const reviewItem = ref<Reviews>()
const resTotal = ref(0)
const route = useRoute()
const routeId = route.params.id as string
const carCd: string = routeId.slice(0, routeId.indexOf('_'))
const carModelCd = routeId
const specificationList = ref<PastGradeListDetail[]>()
const conditionFindSpecificationList = ref<PastGradeListDetail[]>()
const mergedSpecificationList = ref<PastGradeListDetail[]>()
const gradeList = ref<GradeListCategory>()
const allGradeFlg = ref(false)
const transitionSource =
  route.query.transition_source?.toString() ?? TRANSITION_SOURCE.catalog_search
const pastModelList = ref<PastModels>()
const carInfo = ref<CarInfo>()
const currentModel = ref<Model>()
const nuxtApp = useNuxtApp()
const showAllGradeButtonFlg: Ref<boolean> = ref(true)
const pagePosition = useState('page_position')
const beforeIsAllGradeMap = useState('before_is_all_grade_map', () => new Map())
const currentModelSwitchHideFlg = useState('current_model_switch_hide')
const modelLength = ref(0)
const mergedModelLength = ref(0)
const tableWidthPx = useState('table_width_px', () => '132px')
const isUsedCars = ref(false)
const urlParameter = ref<UrlParametersItem>()
const checkUsedCarsResponse = ref<CheckUsedCars>()
const gradeSpecificationResponse = ref<GradeSpecification>()

let brandAndCarNameEng = ''
// モデルTOP取得。後続7本がこの結果に依存するため単独で先に叩く
const { data: modelsResponse } = await useFetch<Models>(
  '/api/model-top/modelTop',
  {
    key: buildAsyncDataKey('model-top', carModelCd),
    cache: 'no-cache',
    query: { car_model_cd: carModelCd },
  }
)
{
  // data が空配列の場合、従来は後続の undefined 参照で実行時エラーになっていたため、
  // 先頭要素の存在まで含めてガードしエラー画面に倒す
  const model = modelsResponse.value?.data?.[0]
  if (model) {
    currentModel.value = model
    carInfo.value = {
      brand_name: model.brand_name,
      car_name: model.car_name,
      model_name: model.model_name,
      images: model.images,
      special_flg: false,
      current_model_flg: model.current_model_flg,
      sales_start_date: model.sales_start_date,
      sales_end_date: model.sales_end_date,
      recent_debut_date_jpn: model.recent_debut_date_jpn,
      usedcar_min_price: model.usedcar_min_price,
      usedcar_max_price: model.usedcar_max_price,
      newcar_price_lower_limit: model.newcar_price_lower_limit,
      newcar_price_upper_limit: model.newcar_price_upper_limit,
    }
    brandAndCarNameEng =
      '/' + model.brand_name_english + '/' + model.car_name_english
  } else {
    throw createError({ fatal: true })
  }
}

// PromiseSettledResult をそのまま返すと、rejected の reason（ofetch の FetchError）が
// payload の直列化に載る。devalue は非 POJO を stringify できず、Nuxt の payload reducer にも
// FetchError 相当が無いため DevalueError になり、ページ全体が 500 になる。
// 失敗を null へ畳んで POJO だけを返す（タプル型を保つため要素ごとに畳む）。
const foldSettled = <T,>(result: PromiseSettledResult<T>): T | null =>
  result.status === 'fulfilled' ? result.value : null

// モデルTOP に依存する7本。元実装の並列取得（Promise.allSettled）をそのまま維持したうえで
// useAsyncData のハンドラ内に入れ、payload 転送でハイドレーション後の再取得を防ぐ
const { data: modelTopBundle } = await useAsyncData(
  buildAsyncDataKey('model-top-bundle', carModelCd),
  async () => {
    const settled = await Promise.allSettled([
      // 中古車が存在するかのチェック
      $fetch<CheckUsedCars>('/api/model-top/checkUsedCars', {
        cache: 'no-cache',
        query: { brand_cd: currentModel.value?.brand_cd },
      }),
      // グレード一覧取得
      $fetch<GradeList>('/api/gradeList', {
        cache: 'no-cache',
        query: {
          car_model_cd: carModelCd,
          by_grade_flg: true,
        },
      }),
      // グレード諸元表取得 現行販売車 or 最新発売モデル
      $fetch<GradeSpecification>('/api/gradeList', {
        cache: 'no-cache',
        query: currentModel.value?.current_model_flg
          ? {
              car_model_cd: carModelCd,
              by_grade_flg: false,
              current_model_flg: true,
            }
          : {
              car_model_cd: carModelCd,
              by_grade_flg: false,
              debut_year: currentModel.value?.recent_debut_year,
              debut_month: currentModel.value?.recent_debut_month,
            },
      }),
      // 車種レビュー（総合評価）取得
      $fetch<RatingsSearch>('/api/model-grade/car-reviews/ratings', {
        cache: 'no-cache',
        params: {
          car_cd: carCd,
        },
      }),
      // 車種レビュー（最新レビュー）取得
      $fetch<ReviewsSearch>('/api/model-grade/car-reviews/reviews', {
        cache: 'no-cache',
        params: {
          car_cd: carCd,
        },
      }),
      // 歴代モデル一覧取得
      $fetch<PastModelInfo>('/api/model-top/models', {
        cache: 'no-cache',
        params: { car_cd: carCd },
      }),
      // シェア内容のURL変換取得
      $fetch<UrlParametersResponse>('/api/catalogs/urlParameters', {
        cache: 'no-cache',
        query: {
          car_cd: carCd,
        },
      }),
    ])
    return [
      foldSettled(settled[0]),
      foldSettled(settled[1]),
      foldSettled(settled[2]),
      foldSettled(settled[3]),
      foldSettled(settled[4]),
      foldSettled(settled[5]),
      foldSettled(settled[6]),
    ] as const
  }
)

if (modelTopBundle.value) {
  // 畳んだタプル型がそのまま効くため、旧実装の index + as キャストは不要
  const [
    checkUsedCarsResult,
    gradeListResult,
    gradeSpecificationResult,
    ratingsResult,
    reviewsResult,
    pastModelsResult,
    urlParametersResult,
  ] = modelTopBundle.value

  // 中古車が存在するかのチェック
  checkUsedCarsResponse.value = checkUsedCarsResult ?? undefined
  // グレード一覧取得
  if (gradeListResult?.data?.catalogs) {
    gradeList.value = gradeListResult.data.catalogs
  }
  // グレード諸元表取得 現行販売車 or 最新発売モデル
  gradeSpecificationResponse.value = gradeSpecificationResult ?? undefined
  // 車種レビュー（総合評価）取得
  if (ratingsResult?.data instanceof Array && ratingsResult.data.length > 0) {
    ratingsItem.value = ratingsResult.data[0]
  }
  // 車種レビュー（最新レビュー）取得
  if (reviewsResult?.data instanceof Array && reviewsResult.data.length > 0) {
    reviewItem.value = reviewsResult.data[0]
    resTotal.value = reviewsResult.total_results_available
  }
  // 歴代モデル一覧取得
  if (pastModelsResult?.data) {
    pastModelList.value = pastModelsResult.data
  }
  // data配列は1つのため[0]を指定
  if (urlParametersResult?.data) {
    urlParameter.value = urlParametersResult.data[0]
  }
}

// 中古車が存在するかのチェック
if (checkUsedCarsResponse.value && checkUsedCarsResponse.value.data) {
  for (const checkUsedCar of checkUsedCarsResponse.value.data) {
    isUsedCars.value =
      checkUsedCar.children.filter(
        (usedCarCd: UsedCarContents) => usedCarCd.car_cd === carCd
      ).length > 0
    if (isUsedCars.value) {
      break
    }
  }
} else {
  isUsedCars.value = false
}

// グレード諸元表取得 現行販売車 or 最新発売モデル
if (
  gradeSpecificationResponse.value &&
  gradeSpecificationResponse.value.data &&
  gradeSpecificationResponse.value.data.catalogs &&
  gradeSpecificationResponse.value.data.catalogs.length > 0
) {
  specificationList.value = gradeSpecificationResponse.value.data.catalogs

  if (
    transitionSource === TRANSITION_SOURCE.condition_find ||
    transitionSource === TRANSITION_SOURCE.pickup
  ) {
    const hasQueryFlg = nuxtApp.$hasGradeSpecificationQuery(route.query)
    showAllGradeButtonFlg.value = hasQueryFlg

    // キーは車種コードと URL の検索条件から組み立てる。リクエストのクエリに
    // makeGradeSpecificationQuery が足す値は車種コードから一意に決まるため、この2つで足りる。
    // 書き換え前に組み立てたうえで sort することで、プロパティの並び順にも
    // makeGradeSpecificationQuery の仕様にもキーが左右されない
    const conditionSpecificationKey = new URLSearchParams()
    for (const [name, value] of Object.entries(route.query)) {
      for (const one of Array.isArray(value) ? value : [value]) {
        conditionSpecificationKey.append(name, one ?? '')
      }
    }
    conditionSpecificationKey.sort()

    // グレード諸元表取得 お好み検索・ピックアップ
    // makeGradeSpecificationQuery は route.query を破壊的に書き換えて同じ参照を返すため、
    // route が持つオブジェクトをそのままリクエストに渡さないようコピーする
    const conditionSpecificationQuery = {
      ...nuxtApp.$makeGradeSpecificationQuery(
        route.query,
        carModelCd,
        currentModel.value?.current_model_flg,
        currentModel.value?.recent_debut_year,
        currentModel.value?.recent_debut_month,
        hasQueryFlg
      ),
    }
    const { data: conditionFindSpecification } =
      await useFetch<GradeSpecification>('/api/gradeList', {
        key: buildAsyncDataKey(
          'model-top-condition-spec',
          carModelCd,
          conditionSpecificationKey.toString()
        ),
        cache: 'no-cache',
        query: conditionSpecificationQuery,
      })

    const conditionFindData = conditionFindSpecification.value?.data
    if (conditionFindData?.catalogs && conditionFindData.catalogs.length > 0) {
      modelLength.value = conditionFindData.total_results_available
      conditionFindSpecificationList.value = conditionFindData.catalogs
      if (hasQueryFlg) {
        mergeSpecificationList(
          conditionFindSpecificationList.value,
          specificationList.value
        )
        showAllGradeButtonFlg.value =
          mergedSpecificationList.value?.length !==
          conditionFindSpecificationList.value?.length
      }
      specificationList.value = conditionFindSpecificationList.value
      // 履歴位置ごとの「全グレード表示」状態の復元は window に依存するため onMounted 側で行う。
      // ここでは復元しない場合の既定値を適用する（allGradeFlg は宣言時の false のまま）
      tableWidthPx.value = calculateTableWidth(modelLength.value)
    } else {
      showAllGradeButtonFlg.value = false
    }
  } else {
    modelLength.value =
      gradeSpecificationResponse.value.data.total_results_available
    tableWidthPx.value = calculateTableWidth(modelLength.value)
  }
}

if (
  transitionSource === TRANSITION_SOURCE.catalog_search ||
  transitionSource === TRANSITION_SOURCE.latest_catalog
) {
  allGradeFlg.value = true
}

function mergeSpecificationList(
  conditionFindSpecification?: PastGradeListDetail[],
  specificationList?: PastGradeListDetail[]
) {
  if (conditionFindSpecification && specificationList) {
    mergedSpecificationList.value = conditionFindSpecification
      .concat(specificationList)
      .filter(
        (element, index, self) =>
          self.findIndex((e) => e.catalog_id === element.catalog_id) === index
      )
    mergedModelLength.value = mergedSpecificationList.value.length
  }
}

function calculateTableWidth(widthPx: number) {
  return (widthPx * 132).toString() + 'px'
}

const changeAllGradeFlg = () => {
  allGradeFlg.value = !allGradeFlg.value
  beforeIsAllGradeMap.value.set(
    window.history.state.position,
    allGradeFlg.value
  )
  if (allGradeFlg.value) {
    specificationList.value = mergedSpecificationList.value
    tableWidthPx.value = calculateTableWidth(mergedModelLength.value)
  } else {
    specificationList.value = conditionFindSpecificationList.value
    tableWidthPx.value = calculateTableWidth(modelLength.value)
  }
}

const goPastModel = () => {
  const eventName = carInfo.value?.current_model_flg
    ? 'CatalogCurrentMdlTopHistoryMdlListTap'
    : 'CatalogPastMdlTopHistoryMdlListTap'
  nuxtApp.$sendEventGaTag(eventName)

  const el = document.getElementById('pastModel')
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
}

useHead({
  title: TITLE_PAGE.model_top,
})

const searchUsedCars = () => {
  const eventName = carInfo.value?.current_model_flg
    ? 'CatalogCurrentMdlTopCarSearchTap'
    : 'CatalogPastMdlTopCarSearchTap'
  nuxtApp.$sendEventGaTag(eventName)

  const sendBrandDetail: SendBrandDetail = {
    name: currentModel.value?.brand_name ?? '',
    brandCd: String(currentModel.value?.brand_cd ?? ''),
  }

  const sendModelGradesDetail: SendModelGradesDetail = {
    carCd: String(currentModel.value?.car_cd ?? ''),
    modelCd: String(currentModel.value?.model_cd ?? ''),
  }

  const sendCars: SendCar = {
    name: currentModel.value?.car_name ?? '',
    integrationCarCd: currentModel.value?.car_cd
      ? nuxtApp.$convertCarCode(currentModel.value?.car_cd.toString()) + '|'
      : '',
  }

  const sendCondition: SendCondition = {
    brand: [sendBrandDetail],
    car: [sendCars],
    modelGrades: [sendModelGradesDetail],
  }
  postWindowBridge('transitionCarList', JSON.stringify(sendCondition))
}

const searchNewcarTop = async () => {
  nuxtApp.$sendEventGaTag('CatalogCurrentMdlTopNewCarSearchTap', {
    brand_name: urlParameter.value?.brand_name,
    vehicle_name: urlParameter.value?.car_name,
  })
  try {
    const url = await nuxtApp.$getSchemaUrl(SCHEMA_URL.newcar_top)
    window.open(url, '_blank')
  } catch (error) {
    console.error('[Component] Failed to open new car top:', error)
  }
}

onMounted(() => {
  const screenName = carInfo.value?.current_model_flg
    ? 'CatalogCurrentModelTop'
    : 'CatalogModelPastTop'
  const data = { Screen: screenName, ContentGroup: 'カタログ' }
  postWindowBridge('gtagApp', JSON.stringify(data))
  postWindowBridge('toggleCloseButton', false)
  postWindowBridge('toggleTopButton', false)
  postWindowBridge('toggleShareButton', true)
  postWindowBridge('toggleBottomBar', true)
  postWindowBridge('setAppBarTitle', TITLE_PAGE.model_top)
  postWindowBridge('toggleFavoriteButton', true)
  isLoading.value = false
  pagePosition.value = window.history.state.position
  currentModelSwitchHideFlg.value = false

  // お好み検索・ピックアップ経由で諸元表を取得できた場合、履歴位置ごとに保持した
  // 「全グレード表示」状態を復元する。window.history に依存するため setup ではなくここで判定する
  if (conditionFindSpecificationList.value?.length) {
    if (
      beforeIsAllGradeMap.value.get(window.history.state.position) &&
      mergedSpecificationList.value
    ) {
      allGradeFlg.value = true
      beforeIsAllGradeMap.value.set(window.history.state.position, true)
      specificationList.value = mergedSpecificationList.value
      tableWidthPx.value = calculateTableWidth(mergedModelLength.value)
    } else {
      allGradeFlg.value = false
      beforeIsAllGradeMap.value.set(window.history.state.position, false)
      tableWidthPx.value = calculateTableWidth(modelLength.value)
    }
  }

  if (urlParameter.value) {
    useShareContents().value.subject = `${urlParameter.value.brand_name} ${urlParameter.value.car_name}`
    useShareContents().value.bodyText = `${
      useRuntimeConfig().public.WEB_REVIEW_URL
    }/${urlParameter.value.brand_dir}/${urlParameter.value.car_dir}/model${
      String(carModelCd).split('_')[1]
    }/`
  }
})
</script>
<template>
  <div class="contents page-contents">
    <DatadisplayGlobalCarInfoCassette
      v-if="carInfo"
      :car-info="carInfo"
      :is-model-grade="false"
    />
    <div class="btn-area-basic-tab shadow-sm">
      <button
        v-if="isUsedCars"
        class="btnred rounded"
        @click="searchUsedCars()"
      >
        <div class="btnred-area">
          <a>中古車検索</a>
        </div>
      </button>
      <button
        v-if="
          carInfo?.current_model_flg &&
          transitionSource !== TRANSITION_SOURCE.newcar_search
        "
        class="btnwhite rounded"
        @click="searchNewcarTop()"
      >
        <div class="btnwhite-area">
          <a>新車検索</a>
        </div>
      </button>
      <button class="btnwhite rounded" @click="goPastModel()">
        <div class="btnwhite-area">
          <a class="btn-name">歴代モデル一覧</a>
          <img class="arrow-rorate" src="@/assets/image/icon/arrow_right.svg" />
        </div>
      </button>
    </div>
    <div class="grade-detail">
      <div v-if="specificationList" class="grade-detail-index-title">
        <span v-if="transitionSource === TRANSITION_SOURCE.condition_find"
          >諸元表</span
        >
        <span v-else>最新リリース<br />諸元表</span>
        <span
          v-if="
            TRANSITION_SOURCE &&
            (transitionSource === TRANSITION_SOURCE.pickup ||
              transitionSource === TRANSITION_SOURCE.condition_find) &&
            showAllGradeButtonFlg
          "
          class="all-grade-btn"
          ><span class="change-btn">全てのグレードを表示</span>
          <label class="switch">
            <div
              class="slide"
              :class="{ checked: allGradeFlg }"
              @click="changeAllGradeFlg"
            /> </label
        ></span>
      </div>
    </div>
    <DatadisplayModelTopGradeListTable
      v-if="specificationList"
      :is-used-cars="isUsedCars"
      :specification-list="specificationList"
      :current-model-flg="carInfo?.current_model_flg"
    />
    <DatadisplayGlobalGradeList
      v-if="gradeList"
      :common-contents-flg="false"
      :grade-list="gradeList"
      :current-model-flg="carInfo?.current_model_flg"
    />
    <DatadisplayModelTopPastModelList
      v-if="pastModelList"
      :past-models="pastModelList"
      :car-model-cd="carModelCd"
      :current-model-flg="carInfo?.current_model_flg"
    />
    <DatadisplayGlobalCarReviewCassette
      v-if="ratingsItem && reviewItem"
      :brand-and-car-name-eng="brandAndCarNameEng"
      :ratings-item="ratingsItem"
      :review-item="reviewItem"
      :res-total="resTotal"
      :current-model-flg="carInfo?.current_model_flg"
      :transition-grade-list-flg="false"
    />
    <DatadisplayModelTopRelatedPosts
      :current-model-flg="carInfo?.current_model_flg"
    />
  </div>
  <SpinnerCircleSpinner :is-loading="isLoading" />
  <ButtonBackToTop />
</template>
<style scoped>
.page-contents {
  margin-bottom: 70px;
}
.bi {
  font-size: 20px;
}
.btn-area-basic-tab {
  padding-bottom: 24px;
  padding-top: 8px;
  background-color: #ffffff;
}
.btnred {
  width: 90%;
  height: 44px;
  display: flex;
  margin: 0 auto 8px auto;
  align-items: center;
  background: #df2020;
  color: #ffffff;
  text-align: center;
  font-weight: bold;
  border: none;
}
.btnred-area {
  width: fit-content;
  margin: 15px auto;
  color: #ffffff;
}
.btnwhite {
  width: 90%;
  height: 44px;
  display: flex;
  margin: 0 auto 8px auto;
  align-items: center;
  background: #ffffff;
  color: #df2020;
  text-align: center;
  font-weight: bold;
  border: 1px solid #df2020;
}
.btnwhite-area {
  width: fit-content;
  margin: 0 auto;
  color: #df2020;
}
.btnwhite a {
  color: #df2020;
}
.grade-detail {
  margin-top: 32px;
}
.grade-detail-index-title {
  padding-left: 16px;
  padding-right: 16px;
  font-size: 20px;
  font-weight: bold;
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
}

.all-grade-btn {
  align-items: center;
  display: flex;
}

.change-btn {
  font-size: 11px;
  font-weight: bold;
  margin-right: 6px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;
}

.switch input {
  display: none;
}
.slide {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #e9e9e9;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 22px;
}

.slide:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 2px;
  bottom: 2px;
  background-color: #ffffff;
  -webkit-transition: 0.4s;
  transition: 0.4s;
  border-radius: 50%;
}

.slide.checked {
  background-color: #df2020;
}

.slide.checked::before {
  -webkit-transform: translateX(18px);
  -ms-transform: translateX(18px);
  transform: translateX(18px);
}

.arrow-rorate {
  transform: rotate(90deg);
  width: 24px;
  height: 24px;
}

.btn-name {
  margin-right: 16px;
  margin-left: 30px;
  text-decoration: none;
}
</style>
